import { api } from "@/lib/api";
import {
  CreateServiceProvider,
  createServiceProviderToDto,
  ServiceProviderDomain,
  ServiceProviderDTOSchema,
  serviceProviderFromDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getAllServiceProvidersQueryOptions,
  getServiceProvidersQueryOptions,
} from "./get.hooks";

const toastDomainMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.serviceProviderProviderCreated",
  provider: "toast.serviceProviderServiceCreated",
};

const toastDomainErrorMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.createServiceProviderProviderError",
  provider: "toast.createServiceProviderServiceError",
};

export function useCreateServiceProvider(
  domain: ServiceProviderDomain,
  domainId: string,
  opts?: {
    onSuccess?: (serviceProvider: CreateServiceProvider) => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (serviceProvider: CreateServiceProvider) => {
      const dto = createServiceProviderToDto(serviceProvider);
      const result = await api.post(
        "/api/service-providers",
        dto,
        ServiceProviderDTOSchema,
      );
      return serviceProviderFromDto(result);
    },
    onSuccess: (serviceProvider) => {
      queryClient.setQueryData(
        getServiceProvidersQueryOptions(domain, domainId).queryKey,
        (old) => [...(old ?? []), serviceProvider],
      );
      queryClient.setQueryData(
        getAllServiceProvidersQueryOptions.queryKey,
        (old) => [...(old ?? []), serviceProvider],
      );
      toast({
        title: toastDomainMessageMap[domain],
        variant: "success",
      });
      opts?.onSuccess?.(serviceProvider);
    },
    onError: () => {
      toast({
        title: toastDomainErrorMessageMap[domain],
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
